import {
  Directive,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy
} from '@angular/core';
import { NgForage } from 'ngforage';
import { SignAssociation } from './sign-association';
import { CollectionHelper } from '../../../../helpers/collection-helper';
import AssetHelper from '../../../../helpers/asset-helper';
import Asset from '../../../../model/asset';
import { StringHelper } from '../../../../helpers/string-helper';

@Directive()
export abstract class EditAssociationComponent<T extends SignAssociation>
  implements OnInit, OnDestroy {
  @Input()
  signId: number;
  @Input()
  assocObject: T;
  @Output()
  saveSuccessEvent = new EventEmitter<T>();

  loading: boolean = true;
  saving: boolean = false;

  associationType: string;

  constructor(protected ngForage: NgForage) {}

  ngOnInit(): void {
    this.initializeAssociationType();
    this.initializeAssocObject();
  }

  ngOnDestroy(): void {
    this.cleanupOfflineAssets();
  }

  protected abstract initializeAssociationType(): void;

  protected abstract initializeAssocObject(): void;

  close(): void {
    this.saveSuccessEvent.next();
  }

  immoInfoChange(value: string): void {
    this.assocObject.immoInfo = value;
  }

  graphicalInfoChange(value: string): void {
    this.assocObject.graphicalInfo = value;
  }

  atelierInfoChange(value: string): void {
    this.assocObject.atelierInfo = value;
  }

  installerInfoChange(value: string): void {
    this.assocObject.installerInfo = value;
  }

  async saveAssocObject(): Promise<void> {
    this.saving = true;
    await this.fillOfflineAssetsBase64();
    const result = await this.getSavePromise();
    this.saveSuccessEvent.next(result);
  }

  private async fillOfflineAssetsBase64(): Promise<void> {
    if (!CollectionHelper.isPopulated(this.assocObject.offlineExamples)) {
      return;
    }
    const promises = this.assocObject.offlineExamples.map((a: Asset) =>
      this.fillOfflineAssetBase64(a)
    );
    await Promise.all(promises);
  }

  private async fillOfflineAssetBase64(offlineAsset: Asset): Promise<void> {
    if (!StringHelper.isEmpty(offlineAsset.base64)) {
      return;
    }
    const file = await this.ngForage.getItem<File>(
      AssetHelper.getStorageKeyFor(offlineAsset.localFileUuid)
    );
    offlineAsset.base64 = await AssetHelper.extractBase64(file);
  }

  protected abstract getSavePromise(): Promise<T>;

  private async cleanupOfflineAssets(): Promise<void> {
    if (
      this.assocObject != null &&
      CollectionHelper.isPopulated(this.assocObject.offlineExamples)
    ) {
      const promises = this.assocObject.offlineExamples.map(a =>
        this.ngForage.removeItem(AssetHelper.getStorageKeyFor(a.localFileUuid))
      );
      await Promise.all(promises);
    }
  }
}
